import PageHero from "@/components/PageHero";
import ImageFrame from "@/components/ImageFrame";
import CTABand from "@/components/CTABand";
import { Reveal, MaskedLines, ChapterLabel } from "@/components/motion";
import { IMAGES } from "@/data/content";

const PRINCIPLES = [
  {
    title: "Faglig ærlighed",
    text: "Her får du et ærligt svar på, hvad der vides, og hvad der ikke vides. Ingen løfter, ingen quick fixes, ingen overdrivelser.",
  },
  {
    title: "Mennesket først",
    text: "Træningen tilpasses mennesket, aldrig omvendt. To forløb hos Center for Hjerneregulering er sjældent ens.",
  },
  {
    title: "Regulering frem for præstation",
    text: "Målet er ikke at tvinge hjernen ind i én forudbestemt tilstand, men at arbejde med fleksibilitet, tilpasning og regulering.",
  },
  {
    title: "Videnskabelig nysgerrighed",
    text: "Forskningen følges tæt, og praksis udfordres, når viden udvikler sig.",
  },
];

const Om = () => (
  <main data-testid="page-om">
    <PageHero
      testId="hero-om"
      overline="Om centret"
      title={["Et center bygget", "på nysgerrighed."]}
      lead="Center for Hjerneregulering er skabt ud fra en enkel overbevisning: Hjernen fortjener samme omsorg og træning som kroppen."
    />

    <section className="border-b border-hairline">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <ImageFrame
              src={IMAGES.founder}
              alt="Ghazal Samii ved bordet med neurofeedback-materiale og EEG-hoved"
              caption="Fig. 01 — Ghazal Samii, grundlægger"
              testId="om-founder-img"
              ratio="aspect-[3/4]"
              tone="none"
              parallax={false}
            />
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted">Grundlægger</p>
          </Reveal>
          <h2 className="mt-6 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            <MaskedLines mode="view" lines={["Ghazal Samii"]} />
          </h2>
          <Reveal delay={0.15} className="mt-8 space-y-5 text-base leading-relaxed text-graphite">
            <p className="font-serif text-2xl font-light italic leading-snug text-charcoal sm:text-3xl">
              &ldquo;Jeg fandt neurofeedback, da jeg ledte efter noget, der kunne hjælpe mit eget
              barn.&rdquo;
            </p>
            <p>
              Min interesse for hjernens regulering begyndte, da jeg som mor søgte nye muligheder for at
              hjælpe mit barn. Det førte mig til neurofeedback og blev begyndelsen på en faglig rejse, der
              siden har ført til uddannelse og arbejdet med hjernens selvregulering.
            </p>
            <p>
              Undervejs voksede min nysgerrighed på hjernens evne til at regulere, tilpasse og forandre
              sig. Den nysgerrighed blev grundlaget for Center for Hjerneregulering.
            </p>
          </Reveal>
          <Reveal delay={0.25} className="mt-10 border-t border-hairline pt-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted">Faglig baggrund</p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted">
              Ghazal Samii er uddannet EEG- og neurofeedbacktræner og arbejder med ILF Neurofeedback efter
              Othmer-metoden.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="border-b border-hairline bg-surface">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Reveal>
          <ChapterLabel number="01" label="Principper" />
        </Reveal>
        <h2 className="mt-8 font-serif text-3xl leading-[1.1] tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
          <MaskedLines mode="view" lines={["Fire ting centret", "aldrig går på", "kompromis med"]} />
        </h2>
        <div className="mt-12 grid grid-cols-1 gap-px border border-sand bg-sand sm:grid-cols-2">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06} className="bg-canvas">
              <div className="h-full p-8 transition-colors duration-300 hover:bg-subtle lg:p-10">
                <p className="font-mono text-[11px] tracking-[0.2em] text-muted">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-4 font-serif text-2xl tracking-tight text-charcoal">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-graphite">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section>
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Reveal>
          <ImageFrame
            src={IMAGES.building}
            alt="Center for Hjerneregulerings bygning set fra Gudenåen"
            caption="Fig. 02 — Centrets rammer"
            testId="om-center-img"
            ratio="aspect-[16/9]"
            tone="none"
          />
        </Reveal>
        <Reveal delay={0.15} className="mt-10 max-w-2xl">
          <p className="text-lg font-light leading-relaxed text-graphite">
            Center for Hjerneregulering har bevidst skabt rolige rammer med minimal unødvendig stimulation.
            Ingen klinisk stemning, ingen overflødig støj. Bare ro, lys og tid til det, der betyder noget.
          </p>
        </Reveal>
      </div>
    </section>

    <CTABand
      title="Book en indledende samtale"
      sub="Træning og samtaler foregår efter aftale. Den indledende samtale er uforpligtende og foregår i dit tempo."
    />
  </main>
);

export default Om;
